'use client';

import { createContext, useContext } from 'react';
import type { SidePref } from './useRushMidMatch';

/**
 * The party-lobby state + actions. Provided by RushMidGame when the snapshot is PARTY and
 * consumed by the games home + its squad strip via useRushMidParty() — so a new party ability
 * is one field on the value here, not a prop threaded through RushMidGame → GamesHomeScreen →
 * child. null inside the context means a plain (non-party) home.
 */
export interface RushMidPartyMemberView {
  userId: string;
  name: string;
  /** has this member confirmed they're ready — the party auto-starts once everyone is. */
  ready: boolean;
  /** can this member cover the current ticket — the backend blocks start otherwise, so the
   *  UI surfaces who's short before then (and blocks their ready toggle). */
  canAfford: boolean;
}

export interface RushMidPartyValue {
  code: string;
  isLeader: boolean;
  leaderUserId: string;
  /** the caller's own userId — to mark "you" in the roster + read your ready state. */
  youUserId: string;
  members: RushMidPartyMemberView[];
  mode: number;
  ticket: number;
  side: SidePref;
  /** ranked (default) or a no-stakes vs-bots practice match for the whole party */
  matchMode?: 'ranked' | 'bots';
  allowedModes: number[];
  onConfig: (mode: number, ticket: number, side: SidePref, matchMode: 'ranked' | 'bots') => void;
  /** toggle your own ready flag; the party auto-starts when all members are ready. */
  onReady: (ready: boolean) => void;
  onLeave: () => void;
  onKick: (userId: string) => void;
  onShare: () => void;
  sharing?: boolean;
}

const RushMidPartyContext = createContext<RushMidPartyValue | null>(null);

export const RushMidPartyProvider = RushMidPartyContext.Provider;

/** The current party, or null in a plain (non-party) lobby. */
export const useRushMidParty = (): RushMidPartyValue | null => useContext(RushMidPartyContext);
